import React, { useId, useMemo } from "react";

interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
  className?: string;
}

/** Area sparkline in the redesign style: green when the last value is up, red when down. */
export const Sparkline: React.FC<SparklineProps> = ({
  values,
  width = 80,
  height = 32,
  className = "",
}) => {
  const gradientId = useId();
  const shape = useMemo(() => {
    if (values.length < 2) return null;
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const step = width / (values.length - 1);
    const points = values.map((v, i) => [
      i * step,
      height - 1 - ((v - min) / span) * (height - 2),
    ]);
    const line = points.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join("");
    return { line, area: `${line}L${width},${height}L0,${height}Z` };
  }, [values, width, height]);

  if (!shape) return <div style={{ width, height }} className={className} />;

  const up = values[values.length - 1] >= values[0];
  const color = up ? "#22c55e" : "#ef4444";

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      aria-hidden
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={shape.area} fill={`url(#${gradientId})`} />
      <path d={shape.line} fill="none" stroke={color} strokeWidth={1.5} />
    </svg>
  );
};
